import React from 'react';
import './DataSourceIndicator.css';

interface DataSource {
  source: string;
  description: string;
  lastUpdated: string;
  isDemo: boolean;
  additionalInfo?: Record<string, any>;
}

interface DataSourceIndicatorProps {
  dataSource?: DataSource;
  showDetails?: boolean;
  size?: 'small' | 'medium' | 'large';
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'inline';
}

const DataSourceIndicator: React.FC<DataSourceIndicatorProps> = ({
  dataSource,
  showDetails = false,
  size = 'small',
  position = 'top-right'
}) => {
  if (!dataSource) return null;

  const getIndicatorClass = () => {
    const baseClass = 'data-source-indicator';
    const sizeClass = `${baseClass}--${size}`;
    const positionClass = `${baseClass}--${position}`;
    const typeClass = dataSource.isDemo ? `${baseClass}--demo` : `${baseClass}--live`;
    
    return `${baseClass} ${sizeClass} ${positionClass} ${typeClass}`;
  };

  const getIndicatorText = () => {
    if (dataSource.isDemo) {
      return size === 'small' ? 'DEMO' : 'Demo Data';
    }
    return size === 'small' ? 'LIVE' : 'Live Data';
  };

  const getIcon = () => {
    if (dataSource.isDemo) {
      return '[WARNING]';
    }
    return '[OK]';
  };

  return (
    <div className={getIndicatorClass()}>
      <div className="data-source-indicator__badge">
        {size !== 'small' && <span className="data-source-indicator__icon">{getIcon()}</span>}
        <span className="data-source-indicator__text">{getIndicatorText()}</span>
      </div>
      
      {showDetails && (
        <div className="data-source-indicator__tooltip">
          <div className="data-source-indicator__tooltip-content">
            <h4>Data Source Information</h4>
            <p><strong>Type:</strong> {dataSource.description}</p>
            <p><strong>Last Updated:</strong> {new Date(dataSource.lastUpdated).toLocaleString()}</p>
            
            {dataSource.additionalInfo && (
              <div className="data-source-indicator__additional-info">
                {Object.entries(dataSource.additionalInfo).map(([key, value]) => (
                  <p key={key}>
                    <strong>{key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}:</strong> {String(value)}
                  </p>
                ))}
              </div>
            )}
            
            {dataSource.isDemo && (
              <div className="data-source-indicator__warning">
                <p><strong>[NOTICE]:</strong> This data is for demonstration purposes only and does not reflect real business metrics.</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DataSourceIndicator;